import type { OrganizationType } from '@form-factory/schema';
import { Currency } from '@form-factory/schema/enums';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { useLingui } from '@lingui/react/macro';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, cn, Tooltip } from '@utima/ui';
import { Repeat } from 'lucide-react';
import { useMemo } from 'react';

import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';
import { useOrganizationChange } from '@/hooks/useOrganizationChange';

import { useOrganizationMutation } from './useOrganizationMutation';

const columnHelper = createColumnHelper<OrganizationType>();

export function useOrganizationsCols() {
  const { t } = useLingui();
  const {
    remove: { mutateAsync: remove },
  } = useOrganizationMutation();

  const handleOrganizationChange = useOrganizationChange();

  return useMemo(
    () => [
      columnHelper.accessor(row => row.id, {
        id: 'id',
        header: t`ID`,
      }),
      columnHelper.accessor(row => row.name, {
        id: 'name',
        header: t`Name`,
        meta: {
          sortable: 'createdAt',
        },
      }),
      columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        header: t`Created`,
        cell: row => formatUtils.date(row.getValue(), DateFormat.DateTime),
        meta: {
          sortable: 'createdAt',
        },
      }),
      columnHelper.accessor(row => row.monthlyUsageLimit, {
        id: 'monthlyUsage',
        header: t`Monthly usage`,
        cell: ({ row }) => {
          const current = row.original.currentMonthlyUsage ?? 0;
          const limit = row.original.monthlyUsageLimit ?? 0;
          const orgCurrency = row.original.currency ?? Currency.USD;

          if (!limit) {
            return <span className='font-semibold'>-</span>;
          }

          return (
            <span
              className={cn(
                'inline-flex gap-1 items-baseline',
                current > limit ? 'text-red-600' : 'text-green-600',
              )}
            >
              <span className='font-semibold'>
                {formatUtils.credits(current, orgCurrency)}
              </span>
              /
              <span className='text-xs font-semibold'>
                {formatUtils.credits(limit, orgCurrency)}
              </span>
            </span>
          );
        },
        meta: {
          align: 'right',
          sortable: 'currentMonthlyUsage',
        },
      }),
      columnHelper.accessor(row => row.balance, {
        id: 'balance',
        header: t`Prepaid balance`,
        cell: ({ row }) => {
          const current = row.original.balance ?? 0;
          const orgCurrency = row.original.currency ?? Currency.USD;

          return (
            <span
              className={cn(
                'font-semibold',
                current > 0 ? 'text-green-600' : '',
              )}
            >
              {formatUtils.credits(current, orgCurrency)}
            </span>
          );
        },
        meta: {
          align: 'right',
          sortable: 'balance',
        },
      }),
      columnHelper.accessor(row => row.balance, {
        id: 'balance',
        header: t`Remaining Balance`,
        cell: ({ row }) => {
          const balance = row.original.balance ?? 0;
          const orgCurrency = row.original.currency ?? Currency.USD;

          return (
            <span
              className={cn(
                'inline-flex gap-1 items-baseline',
                balance < 0 ? 'text-red-600' : 'text-green-600',
              )}
            >
              <span className='font-semibold'>
                {formatUtils.currency(balance, orgCurrency)}
              </span>
            </span>
          );
        },
        meta: {
          align: 'right',
        },
      }),

      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <ActionsCell
            onDelete={() => remove({ id: row.original.id })}
            deleteDescription={t`Are you sure you want to delete this organization? Any remaining credits will be permanently lost.`}
            confirmationText={row.original.name}
            confirmationLabel={t`Type the organization name to confirm:`}
          >
            <Tooltip title={t`Switch to organization`}>
              <Button
                data-cta='admin-organizations-switch-to-organization-button'
                size='icon-xs'
                variant='ghost'
                className='text-blue-500'
                outline
                onClick={() => handleOrganizationChange(row.original.id)}
              >
                <Repeat className='size-4' />
              </Button>
            </Tooltip>
          </ActionsCell>
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [remove, handleOrganizationChange, t],
  );
}
